import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import { APPLY } from "@/constants/testIds";

const SPECIALTY_SUGGESTIONS = [
  "Boxing", "Tennis", "Yoga", "Career", "Life coaching",
  "Salsa", "Strength", "Guitar", "Freediving", "Swimming",
  "Language", "Cooking", "Mindfulness", "Pilates", "Public speaking",
];

const empty = {
  name: "",
  headline: "",
  bio: "",
  contact_email: "",
  city: "",
  hourly_rate: "",
  years_experience: "",
  specialties: [],
  languages: "English",
  avatar_url: "",
  cover_url: "",
  sample_link: "",
};

export default function CoachApply() {
  const [form, setForm] = useState(empty);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSpecialty = (s) => {
    setForm((f) => {
      const has = f.specialties.includes(s);
      return { ...f, specialties: has ? f.specialties.filter((x) => x !== s) : [...f.specialties, s].slice(0, 8) };
    });
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (form.headline.trim().length < 8) return "Write a headline of at least 8 characters.";
    if (form.bio.trim().length < 40) return "Bio should be at least 40 characters — clients need context.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) return "Please enter a valid email.";
    if (form.city.trim().length < 2) return "Please enter your city (or 'Online').";
    const rate = Number(form.hourly_rate);
    if (!rate || rate < 15 || rate > 1000) return "Hourly rate must be between $15 and $1000.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus({ state: "error", message: err });
      return;
    }
    setSubmitting(true);
    setStatus({ state: "idle", message: "" });
    try {
      const payload = {
        ...form,
        hourly_rate: Number(form.hourly_rate),
        years_experience: form.years_experience ? Number(form.years_experience) : 3,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/coaches/apply", payload);
      setSubmittedEmail(payload.contact_email);
      setStatus({ state: "success", message: "Application received. We'll email you within 48 hours." });
      setForm(empty);
      toast.success("Application submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setStatus({ state: "error", message: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isDone = status.state === "success";

  return (
    <div data-testid={APPLY.page} className="bg-white min-h-screen">
      <MarketplaceNav />

      <section className="border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="eyebrow">§ Coach onboarding · Beta</div>
            <h1 className="mt-4 font-display font-medium text-[44px] sm:text-[64px] md:text-[80px] leading-[0.95] tracking-[-0.05em] text-slate-900 max-w-[16ch]">
              Coach on your <span className="italic font-light text-[#2563EB]">own terms.</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg max-w-[58ch] leading-relaxed">
              Apply once. We verify you personally. Then set your rates, keep 85%, and start booking real clients — no algorithmic middleman.
            </p>
          </motion.div>

          <div className="mt-12 grid md:grid-cols-3 gap-4 max-w-4xl">
            {[
              { t: "Verified profile", d: "One personal call, then your profile goes live." },
              { t: "Keep 85%", d: "Transparent split. No hidden fees." },
              { t: "Own your clients", d: "Reviews and rebookings follow you, not us." },
            ].map((x) => (
              <div key={x.t} className="rounded-2xl border border-slate-200 p-5 bg-white">
                <Check className="w-4 h-4 text-[#10B981]" strokeWidth={2.6} />
                <div className="mt-3 font-display font-medium text-lg tracking-tight">{x.t}</div>
                <p className="mt-1 text-[13px] text-slate-500 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {isDone ? (
          <motion.div
            data-testid={APPLY.success}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#059669] text-[12px] font-semibold">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
              Application received
            </div>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight text-slate-900">
              Thanks — you're on the list.
            </h2>
            <p className="mt-4 text-slate-500 max-w-[52ch] mx-auto">
              We'll email <span className="font-semibold text-slate-900">{submittedEmail || "you"}</span> within 48 hours to schedule a short verification call.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/marketplace" className="btn-primary h-11 px-5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2">
                Browse the marketplace <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
              </Link>
            </div>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            data-testid={APPLY.form}
            className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name" testId={APPLY.name} value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Ava L." required />
              <Field label="Contact email" type="email" testId={APPLY.email} value={form.contact_email} onChange={(v) => set("contact_email", v)} placeholder="you@domain.com" required />
              <Field label="Headline" testId={APPLY.headline} value={form.headline} onChange={(v) => set("headline", v)} placeholder="One-liner clients will read first" required />
              <Field label="City" testId={APPLY.city} value={form.city} onChange={(v) => set("city", v)} placeholder="e.g. Brooklyn, NY (or 'Online')" required />
              <Field label="Hourly rate (USD)" type="number" testId={APPLY.rate} value={form.hourly_rate} onChange={(v) => set("hourly_rate", v)} placeholder="80" required />
              <Field label="Years of experience" type="number" testId={APPLY.years} value={form.years_experience} onChange={(v) => set("years_experience", v)} placeholder="5" />
            </div>

            <div className="mt-5">
              <FieldLabel>Bio</FieldLabel>
              <textarea
                value={form.bio}
                onChange={(e) => set("bio", e.target.value)}
                data-testid={APPLY.bio}
                rows={5}
                maxLength={2000}
                placeholder="How do you coach? Who is this for? What do sessions look like?"
                className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition resize-y"
                required
              />
              <div className="mt-1 text-[11px] text-slate-400 text-right">{form.bio.length}/2000</div>
            </div>

            <div className="mt-5">
              <FieldLabel>Specialties (pick up to 8)</FieldLabel>
              <div data-testid={APPLY.specialties} className="mt-2 flex flex-wrap gap-2">
                {SPECIALTY_SUGGESTIONS.map((s) => {
                  const on = form.specialties.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSpecialty(s)}
                      className={`h-9 px-4 rounded-full text-[12.5px] font-medium border transition-colors ${
                        on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-5">
              <Field label="Languages (comma separated)" testId={APPLY.languages} value={form.languages} onChange={(v) => set("languages", v)} placeholder="English, Spanish" />
              <Field label="Sample link (optional)" testId={APPLY.sampleLink} value={form.sample_link} onChange={(v) => set("sample_link", v)} placeholder="LinkedIn, YouTube, personal site" />
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-5">
              <Field label="Avatar URL (optional)" testId={APPLY.avatarUrl} value={form.avatar_url} onChange={(v) => set("avatar_url", v)} placeholder="Paste a square photo URL" />
              <Field label="Cover image URL (optional)" testId={APPLY.coverUrl} value={form.cover_url} onChange={(v) => set("cover_url", v)} placeholder="Paste a wide banner URL" />
            </div>

            <div className="mt-8 flex items-center justify-between gap-4">
              <p className="text-[11.5px] text-slate-400 max-w-[46ch] leading-relaxed">
                By applying you agree to a short verification call and to our{" "}
                <a href="/coach-guidelines" target="_blank" rel="noreferrer" className="text-slate-600 hover:text-[#2563EB] underline underline-offset-2">Coach Guidelines</a>,{" "}
                <a href="/trust-safety" target="_blank" rel="noreferrer" className="text-slate-600 hover:text-[#2563EB] underline underline-offset-2">Trust &amp; Safety Policy</a>, and{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-slate-600 hover:text-[#2563EB] underline underline-offset-2">Terms</a>.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid={APPLY.submit}
                className="h-12 px-6 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit application"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>

            {status.state === "error" && (
              <div data-testid={APPLY.error} className="mt-4 text-[13px] text-red-500">
                {status.message}
              </div>
            )}
          </form>
        )}
      </section>
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">{children}</label>;
}

function Field({ label, value, onChange, placeholder, type = "text", testId, required }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        placeholder={placeholder}
        required={required}
        className="mt-2 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition"
      />
    </div>
  );
}
